import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hero banner carousel.
 *
 * The previous version swapped a single <img> by index, so there was nothing to
 * swipe or drag and the dots were decorative. This is a real scroll-snap track:
 * touch swipe and trackpad drag work natively, the dots and arrows drive it,
 * and auto-advance rides on top — pausing whenever the user interacts.
 */
export function BannerCarousel({ banners, intervalMs = 4000 }) {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  const programmatic = useRef(false);
  const resumeAt = useRef(0);

  // Any deliberate interaction (swipe, arrow, dot) holds auto-advance off for a
  // few seconds — otherwise the carousel yanks you off the slide you just chose.
  const holdAuto = useCallback((ms = 7000) => {
    resumeAt.current = Date.now() + ms;
  }, []);

  const scrollTo = useCallback((i) => {
    const el = trackRef.current;
    if (!el) return;
    const n = banners.length;
    if (!n) return;
    holdAuto();
    const next = ((i % n) + n) % n;
    programmatic.current = true;
    el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
    setIdx(next);
    // Let the smooth scroll settle before trusting scroll events again.
    window.setTimeout(() => { programmatic.current = false; }, 450);
  }, [banners.length, holdAuto]);

  // Auto-advance.
  useEffect(() => {
    if (banners.length < 2) return undefined;
    const t = setInterval(() => {
      if (paused.current || document.hidden || Date.now() < resumeAt.current) return;
      setIdx((i) => {
        const next = (i + 1) % banners.length;
        const el = trackRef.current;
        if (el) {
          programmatic.current = true;
          el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
          window.setTimeout(() => { programmatic.current = false; }, 450);
        }
        return next;
      });
    }, intervalMs);
    return () => clearInterval(t);
  }, [banners.length, intervalMs]);

  // Keep the dots in step when the user scrolls the track themselves.
  const onScroll = (e) => {
    if (programmatic.current) return;
    // A scroll we didn't cause means the user is driving.
    holdAuto();
    const el = e.currentTarget;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
    if (i !== idx) setIdx(i);
  };

  // Re-align after a resize so a slide never sits half-scrolled.
  useEffect(() => {
    const onResize = () => {
      const el = trackRef.current;
      if (el) el.scrollTo({ left: idx * el.clientWidth });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [idx]);

  if (!banners.length) return <div className="hero-fallback">🏠</div>;

  const hold = (v) => () => { paused.current = v; };

  return (
    <>
      <div
        ref={trackRef}
        className="banner-track"
        onScroll={onScroll}
        onPointerEnter={hold(true)}
        onPointerLeave={hold(false)}
        onTouchStart={hold(true)}
        onTouchEnd={hold(false)}
      >
        {banners.map((b, i) => (
          <div className="banner-slide" key={b._id || i}>
            <img src={b.image} alt={b.title || `Promotion ${i + 1}`} draggable="false" />
          </div>
        ))}
      </div>

      {banners.length > 1 && (
        <>
          <button className="banner-nav prev" aria-label="Previous banner"
            onClick={() => scrollTo(idx - 1)} onFocus={hold(true)} onBlur={hold(false)}>‹</button>
          <button className="banner-nav next" aria-label="Next banner"
            onClick={() => scrollTo(idx + 1)} onFocus={hold(true)} onBlur={hold(false)}>›</button>
          <div className="banner-dots">
            {banners.map((b, i) => (
              <i key={b._id || i} className={i === idx ? 'on' : ''} role="button" tabIndex={0}
                aria-label={`Go to banner ${i + 1}`}
                onClick={() => scrollTo(i)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollTo(i); } }} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

/**
 * Horizontal scroll rail with arrow buttons. Native scrolling does the work, so
 * touch swipe and trackpad gestures come for free; the arrows page it on
 * desktop and hide when there's nothing further that way.
 *
 * `autoScrollMs` advances it on a timer, looping back to the start at the end.
 * Like the hero carousel it stands down for a while after any deliberate
 * interaction, so it never drags you off something you just scrolled to.
 */
export function Rail({ rows = 1, children, ariaLabel, autoScrollMs = 0 }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });
  const resumeAt = useRef(0);
  const holdAuto = useCallback((ms = 7000) => { resumeAt.current = Date.now() + ms; }, []);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdge({ start: el.scrollLeft <= 2, end: el.scrollLeft >= max - 2 });
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure, children]);

  const page = useCallback((dir) => {
    const el = ref.current;
    if (!el) return;
    holdAuto();
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: 'smooth' });
  }, [holdAuto]);

  // Auto-advance, wrapping back to the start once the end is reached.
  useEffect(() => {
    if (!autoScrollMs) return undefined;
    const t = setInterval(() => {
      const el = ref.current;
      if (!el || document.hidden || Date.now() < resumeAt.current) return;
      if (el.scrollWidth <= el.clientWidth + 4) return;   // nothing to scroll
      const max = el.scrollWidth - el.clientWidth;
      const step = Math.max(240, el.clientWidth * 0.8);
      const next = el.scrollLeft >= max - 4 ? 0 : Math.min(max, el.scrollLeft + step);
      el.scrollTo({ left: next, behavior: 'smooth' });
    }, autoScrollMs);
    return () => clearInterval(t);
    // Deliberately NOT keyed on `children`: that is a fresh array every render,
    // so the interval would be torn down and rebuilt before it could ever fire.
    // The tick reads the live element, so it needs no render-time input.
  }, [autoScrollMs]);

  const hold = (v) => () => { if (v) holdAuto(); };

  return (
    <div className="rail-wrap" onPointerEnter={hold(true)} onTouchStart={hold(true)}>
      <button className="rail-nav prev" onClick={() => page(-1)} disabled={edge.start} aria-label="Scroll left">‹</button>
      <div
        ref={ref}
        className={`rail rail-${rows}row`}
        aria-label={ariaLabel}
        onScroll={(e) => { measure(e); }}
        onWheel={hold(true)}
      >
        {children}
      </div>
      <button className="rail-nav next" onClick={() => page(1)} disabled={edge.end} aria-label="Scroll right">›</button>
    </div>
  );
}

export const Stars = ({ value = 0 }) => {
  const full = Math.round(value);
  return <span className="rv-stars" aria-label={`${value} out of 5`}>{'★'.repeat(full)}{'☆'.repeat(Math.max(0, 5 - full))}</span>;
};

export const fmtReviewDate = (d) => {
  if (!d) return '';
  const t = new Date(d).getTime();
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days < 30) return days <= 1 ? 'recently' : `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

/**
 * One gallery tile — a photo or a short clip.
 *
 * The media is `object-fit: contain` on a neutral plate so every photo keeps
 * its own proportions: cropping to a fixed 4:3 box mangled portrait shots.
 * Clips show their first frame with a play badge rather than autoplaying —
 * a rail of autoplaying video is heavy and distracting. They play with
 * controls once opened full screen.
 */
export function GalleryTile({ item, onOpen }) {
  const isVideo = item.mediaType === 'video';
  const label = item.caption || item.title || (isVideo ? 'Narayan gallery video' : 'Narayan gallery photo');

  return (
    <figure
      className="gal-item"
      style={{ margin: 0, cursor: onOpen ? 'zoom-in' : 'default' }}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(e); } } : undefined}
    >
      {isVideo ? (
        <>
          <video
            src={item.image}
            poster={item.poster || undefined}
            muted
            playsInline
            preload="metadata"
            aria-label={label}
          />
          <span className="gal-play" aria-hidden="true">▶</span>
        </>
      ) : (
        <img src={item.image} alt={label} loading="lazy" />
      )}
      {(item.caption || item.title) && <figcaption className="gal-cap">{item.caption || item.title}</figcaption>}
    </figure>
  );
}

/**
 * Full-screen media viewer. Shared by the landing rail and the gallery page so
 * a tap behaves the same in both places: the whole image, uncropped, with a
 * clear close button, arrow/swipe paging and Escape to exit.
 */
export function Lightbox({ items, index, onClose, onIndex }) {
  const open = index !== null && index !== undefined && items[index];

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex(Math.min(items.length - 1, index + 1));
      if (e.key === 'ArrowLeft') onIndex(Math.max(0, index - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, index, items.length, onClose, onIndex]);

  if (!open) return null;
  const item = items[index];
  const isVideo = item.mediaType === 'video';
  const caption = item.caption || item.title || '';

  return (
    <div className="lb" role="dialog" aria-modal="true" aria-label="Media viewer" onClick={onClose}>
      <button className="lb-close" onClick={onClose} aria-label="Close">✕</button>

      <div className="lb-count">{index + 1} / {items.length}</div>

      {index > 0 && (
        <button className="lb-nav prev" aria-label="Previous"
          onClick={(e) => { e.stopPropagation(); onIndex(index - 1); }}>‹</button>
      )}

      <figure className="lb-stage" onClick={(e) => e.stopPropagation()}>
        {isVideo ? (
          <video key={item._id} src={item.image} poster={item.poster || undefined}
            controls autoPlay playsInline />
        ) : (
          <img src={item.image} alt={caption || 'Gallery photo'} />
        )}
        {caption ? <figcaption>{caption}</figcaption> : null}
      </figure>

      {index < items.length - 1 && (
        <button className="lb-nav next" aria-label="Next"
          onClick={(e) => { e.stopPropagation(); onIndex(index + 1); }}>›</button>
      )}
    </div>
  );
}

/**
 * Reviewer avatar.
 *
 * Google serves these from lh3.googleusercontent.com. Two things go wrong with
 * a plain <img>: some Google CDN responses vary on the Referer header, and a
 * deleted account leaves a URL that 404s — which renders a broken-image icon
 * instead of falling back. So we send no referrer and drop to the initial on
 * any load failure.
 */
export function Avatar({ src, name, className = 'rv-av' }) {
  const [failed, setFailed] = useState(false);
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <span className={className}>
      {src && !failed ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        initial
      )}
    </span>
  );
}
